import { 
  CompleteKundali, 
  KundaliChartData, 
  PlanetName, 
  ZodiacSign, 
  Language 
} from '../types/kundali';
import { calculateShadbala, getBirthUtcDate } from './vedicEngine';
import { CompleteBhavaConditionAssessment } from './bhavaConditionEngine';

export type ChartArchetypeCategory = 
  | 'SATURN_EARTH_PRAGMATIC'
  | 'MARS_SUN_EXECUTIVE'
  | 'MERCURY_VENUS_AGILE'
  | 'JUPITER_MOON_INTUITIVE';

export interface ChartArchetypeProfile {
  category: ChartArchetypeCategory;
  titleEn: string;
  titleHi: string;
  titleHinglish: string;
  toneDescriptionEn: string;
  toneDescriptionHi: string;
  signatureKeywords: string[];
  conversationalStyle: string;
  primaryPlanetaryDrivers: string[];
  elementalComposition: {
    fire: number;
    earth: number;
    air: number;
    water: number;
  };
}

export interface HumanizedProfileNarrative {
  archetype: ChartArchetypeProfile;
  coreNature: {
    en: string;
    hi: string;
    hinglish: string;
  };
  executiveEdge: {
    en: string;
    hi: string;
    hinglish: string;
  };
  emotionalWiring: {
    en: string;
    hi: string;
    hinglish: string;
  };
  decisionStyleUnderPressure: {
    en: string;
    hi: string;
    hinglish: string;
  };
  karmicBlindspotAndFix: {
    en: string;
    hi: string;
    hinglish: string;
  };
  lifeAreaConversationalAdvice: {
    career: { en: string; hi: string; hinglish: string };
    wealth: { en: string; hi: string; hinglish: string };
    relationships: { en: string; hi: string; hinglish: string };
    growth: { en: string; hi: string; hinglish: string };
  };
}

const EARTH_SIGNS: ZodiacSign[] = ['Taurus', 'Virgo', 'Capricorn'];
const FIRE_SIGNS: ZodiacSign[] = ['Aries', 'Leo', 'Sagittarius'];
const AIR_SIGNS: ZodiacSign[] = ['Gemini', 'Libra', 'Aquarius'];
const WATER_SIGNS: ZodiacSign[] = ['Cancer', 'Scorpio', 'Pisces'];

/**
 * Determine dominant astrological archetype from Lagna, Moon, D10, and Shadbala engine
 */
export function identifyChartArchetype(kundali: CompleteKundali): ChartArchetypeProfile {
  const chartData = kundali.charts?.D1;
  const d10Data = kundali.charts?.D10;

  const ascSign = (chartData?.houses?.find(h => h.houseNumber === 1)?.sign || 'Aries') as ZodiacSign;
  const moonPos = chartData?.planets?.find(p => p.name === 'Moon');
  const moonSign = (moonPos?.sign || 'Aries') as ZodiacSign;
  const sunPos = chartData?.planets?.find(p => p.name === 'Sun');
  const marsPos = chartData?.planets?.find(p => p.name === 'Mars');
  const saturnPos = chartData?.planets?.find(p => p.name === 'Saturn');
  const jupiterPos = chartData?.planets?.find(p => p.name === 'Jupiter');
  const mercuryPos = chartData?.planets?.find(p => p.name === 'Mercury');
  const venusPos = chartData?.planets?.find(p => p.name === 'Venus');

  // Compute elemental scores from D1 planets
  let fire = 0;
  let earth = 0;
  let air = 0;
  let water = 0;

  chartData?.planets?.forEach(p => {
    if (p.name === 'Ascendant') return;
    if (FIRE_SIGNS.includes(p.sign)) fire += 1;
    if (EARTH_SIGNS.includes(p.sign)) earth += 1;
    if (AIR_SIGNS.includes(p.sign)) air += 1;
    if (WATER_SIGNS.includes(p.sign)) water += 1;
  });

  // Calculate shadbala if available
  let saturnScore = 0;
  let marsScore = 0;
  let sunScore = 0;
  let jupiterScore = 0;
  let moonScore = 0;
  let mercuryScore = 0;
  let venusScore = 0;

  try {
    const planetsList = kundali.charts?.D1?.planets || kundali.planets || [];
    const housesList = kundali.houses || kundali.charts?.D1?.houses || [];
    const shadbala = kundali.shadbala || calculateShadbala(planetsList, housesList, {
      birthDate: getBirthUtcDate(kundali.birthDetails),
      latitude: kundali.birthDetails.latitude,
      longitude: kundali.birthDetails.longitude
    });
    saturnScore = shadbala.planets?.find(s => s.planet === 'Saturn')?.totalRupas || 5.0;
    marsScore = shadbala.planets?.find(s => s.planet === 'Mars')?.totalRupas || 5.0;
    sunScore = shadbala.planets?.find(s => s.planet === 'Sun')?.totalRupas || 5.0;
    jupiterScore = shadbala.planets?.find(s => s.planet === 'Jupiter')?.totalRupas || 5.0;
    moonScore = shadbala.planets?.find(s => s.planet === 'Moon')?.totalRupas || 5.0;
    mercuryScore = shadbala.planets?.find(s => s.planet === 'Mercury')?.totalRupas || 5.0;
    venusScore = shadbala.planets?.find(s => s.planet === 'Venus')?.totalRupas || 5.0;
  } catch (e) {
    saturnScore = 5.0;
    marsScore = 5.0;
    sunScore = 5.0;
    jupiterScore = 5.0;
  }

  // Archetype point aggregation
  let saturnEarthPoints = 0;
  let marsSunPoints = 0;
  let mercuryVenusPoints = 0;
  let jupiterMoonPoints = 0;

  // 1. Lagna sign check
  if (EARTH_SIGNS.includes(ascSign) || ascSign === 'Capricorn' || ascSign === 'Aquarius') saturnEarthPoints += 4;
  if (FIRE_SIGNS.includes(ascSign) || ascSign === 'Scorpio') marsSunPoints += 4;
  if (ascSign === 'Gemini' || ascSign === 'Virgo' || ascSign === 'Libra' || ascSign === 'Taurus') mercuryVenusPoints += 4;
  if (ascSign === 'Cancer' || ascSign === 'Pisces' || ascSign === 'Sagittarius') jupiterMoonPoints += 4;

  // 2. Moon sign check
  if (EARTH_SIGNS.includes(moonSign)) saturnEarthPoints += 3;
  if (FIRE_SIGNS.includes(moonSign) || moonSign === 'Scorpio') marsSunPoints += 3;
  if (AIR_SIGNS.includes(moonSign)) mercuryVenusPoints += 3;
  if (WATER_SIGNS.includes(moonSign)) jupiterMoonPoints += 3;

  // 3. 10th house / Career engine in D1 & D10
  const d1H10Lord = chartData?.houses?.find(h => h.houseNumber === 10)?.signLord;
  if (['Saturn'].includes(d1H10Lord || '')) saturnEarthPoints += 2;
  if (['Sun', 'Mars'].includes(d1H10Lord || '')) marsSunPoints += 2;
  if (['Mercury', 'Venus'].includes(d1H10Lord || '')) mercuryVenusPoints += 2;
  if (['Jupiter', 'Moon'].includes(d1H10Lord || '')) jupiterMoonPoints += 2;

  // 4. Shadbala potency weights
  if (saturnScore > 6.2) saturnEarthPoints += 3;
  if (marsScore > 6.2 || sunScore > 6.2) marsSunPoints += 3;
  if (mercuryScore > 6.2 || venusScore > 6.2) mercuryVenusPoints += 3;
  if (jupiterScore > 6.2 || moonScore > 6.2) jupiterMoonPoints += 3;

  // Evaluate winning archetype
  const scores = [
    { cat: 'SATURN_EARTH_PRAGMATIC' as ChartArchetypeCategory, score: saturnEarthPoints },
    { cat: 'MARS_SUN_EXECUTIVE' as ChartArchetypeCategory, score: marsSunPoints },
    { cat: 'MERCURY_VENUS_AGILE' as ChartArchetypeCategory, score: mercuryVenusPoints },
    { cat: 'JUPITER_MOON_INTUITIVE' as ChartArchetypeCategory, score: jupiterMoonPoints }
  ];

  scores.sort((a, b) => b.score - a.score);
  const winningCategory = scores[0].cat;

  const totalElem = Math.max(1, fire + earth + air + water);
  const composition = {
    fire: Math.round((fire / totalElem) * 100),
    earth: Math.round((earth / totalElem) * 100),
    air: Math.round((air / totalElem) * 100),
    water: Math.round((water / totalElem) * 100)
  };

  switch (winningCategory) {
    case 'SATURN_EARTH_PRAGMATIC':
      return {
        category: 'SATURN_EARTH_PRAGMATIC',
        titleEn: 'Pragmatic Builder & Grounded Realist (Saturn-Earth Core)',
        titleHi: 'व्यावहारिक रचनाकार एवं ठोस यथार्थवादी (शनि-पृथ्वी तत्व)',
        titleHinglish: 'Solid Builder & Grounded Pragmatist (Thehraav & System First)',
        toneDescriptionEn: 'Measured, pragmatic, grounded, structured, and no-nonsense.',
        toneDescriptionHi: 'धैर्यवान, ठोस, यथार्थवादी, गणनात्मक और व्यवस्थित स्वभाव।',
        signatureKeywords: ['Thehraav', 'Thos Faisle', 'Hisaab-Kitaab', 'Zero Drama', 'System-First', 'Dheere Magar Pakka'],
        conversationalStyle: 'Direct, calm, facts-first, zero sugarcoating, value-driven execution.',
        primaryPlanetaryDrivers: ['Saturn', 'Earth Signs (Taurus/Virgo/Capricorn)'],
        elementalComposition: composition
      };

    case 'MARS_SUN_EXECUTIVE':
      return {
        category: 'MARS_SUN_EXECUTIVE',
        titleEn: 'High-Agency Commander & Front-Foot Driver (Mars-Sun Core)',
        titleHi: 'तेजस्वी नेतृत्व एवं साहसी कर्ता (सूर्य-मंगल अग्नि तत्व)',
        titleHinglish: 'High-Agency Leader & Front-Foot Fighter (Junoon & Clear Boundaries)',
        toneDescriptionEn: 'High agency, direct, executive, action-oriented, and decisive.',
        toneDescriptionHi: 'साहसी, स्पष्टवादी, नेतृत्व-सम्पन्न और त्वरित निर्णयकारी।',
        signatureKeywords: ['Junoon', 'Clear Boundaries', 'Ownership', 'Front-Foot', 'Self-Driven', 'Seedha Impact'],
        conversationalStyle: 'High-energy, assertive, straight-talking, outcome-focused.',
        primaryPlanetaryDrivers: ['Sun', 'Mars', 'Fire Signs (Aries/Leo/Sagittarius)'],
        elementalComposition: composition
      };

    case 'MERCURY_VENUS_AGILE':
      return {
        category: 'MERCURY_VENUS_AGILE',
        titleEn: 'Sharp Strategist & Socially Agile Negotiator (Mercury-Venus Core)',
        titleHi: 'कुशाग्र रणनीतिकार एवं वाक्-चतुर सूत्रधार (बुध-शुक्र वायु/पृथ्वी तत्व)',
        titleHinglish: 'Sharp Strategist & Socially Agile Multi-Hyphenate (Smart Jugaad & Articulation)',
        toneDescriptionEn: 'Sharp, conversational, analytical yet socially agile, multifaceted.',
        toneDescriptionHi: 'तीक्ष्ण बुद्धि, वाकपटु, व्यवहार कुशल और त्वरित समाधानकर्ता।',
        signatureKeywords: ['Smart Jugaad', 'Clear Articulation', 'Networking', 'Balanced Approach', 'Dimaag & Finesse', 'Multitasking'],
        conversationalStyle: 'Witty, insightful, articulate, pragmatic problem-solver with flair.',
        primaryPlanetaryDrivers: ['Mercury', 'Venus', 'Gemini/Virgo/Libra/Taurus'],
        elementalComposition: composition
      };

    case 'JUPITER_MOON_INTUITIVE':
    default:
      return {
        category: 'JUPITER_MOON_INTUITIVE',
        titleEn: 'Intuitive Mentor & Deeply Principled Anchor (Jupiter-Moon Core)',
        titleHi: 'सहज ज्ञान-संपन्न मार्गदर्शक एवं निष्ठावान स्तम्भ (गुरु-चंद्र जल तत्व)',
        titleHinglish: 'Intuitive Guide & Principled Legacy-Builder (Apnapan & Vishwas)',
        toneDescriptionEn: 'Intuitive, principled, observant, deeply loyal, value-anchored.',
        toneDescriptionHi: 'गंभीर, सहज ज्ञानी, सत्यनिष्ठ, संवेदनशील और दीर्घकालिक मार्गदर्शक।',
        signatureKeywords: ['Apnapan', 'Vishwas', 'Long-Term Legacy', 'Guiding Force', 'Antar-Drishti', 'Maryada'],
        conversationalStyle: 'Warm, thoughtful, visionary, grounded in timeless ethics and trust.',
        primaryPlanetaryDrivers: ['Jupiter', 'Moon', 'Water/Dual Signs (Cancer/Pisces/Sagittarius)'],
        elementalComposition: composition
      };
  }
}

/**
 * Generate humanized, culturally authentic Indian conversational narratives (Hinglish/Hindi/English)
 */
export function generateHumanizedProfile(kundali: CompleteKundali): HumanizedProfileNarrative {
  const archetype = identifyChartArchetype(kundali);
  const chartData = kundali.charts?.D1;
  const ascHouse = chartData?.houses?.find(h => h.houseNumber === 1);
  const ascSign = (ascHouse?.sign || 'Aries') as ZodiacSign;
  const moonPos = chartData?.planets?.find(p => p.name === 'Moon');
  const moonSign = (moonPos?.sign || 'Aries') as ZodiacSign;
  const moonNak = moonPos?.nakshatra || 'Ashwini';

  if (archetype.category === 'SATURN_EARTH_PRAGMATIC') {
    return {
      archetype,
      coreNature: {
        en: `Your chart operates on a grounded, structural frequency. With ${ascSign} ascendant and strong Earth/Saturn foundations, you don't get swayed by fleeting hype or impulsive excitement. You believe in tangible results, clear milestones, and measurable execution. For you, consistency beats noisy motivation every single time.`,
        hi: `आपकी कुंडली का मूल स्वभाव एक ठोस, व्यावहारिक धरातल पर आधारित है। ${ascSign} लग्न और शनि-पृथ्वी तत्व के प्रभाव से आप हवाई दावों या तात्कालिक उत्साह में नहीं बहते। आपका विश्वास ठोस परिणामों, व्यवस्थित योजना और निरंतर अनुशासन में है। आपके लिए "धीमे मगर पक्के" कदम ही वास्तविक सफलता की कुंजी हैं।`,
        hinglish: `Aapki kundali ka main base ekdum solid aur grounded hai. ${ascSign} lagna aur strong Saturn presence ki wajah se aap bina hisaab-kitaab ke koi bada risk nahi lete. Hype aur show-off se door, aapka focus 'zero drama' aur real execution par rehta hai. Aapka formula seedha hai: jo system banaya hai, uspe tikke raho aur result apne aap aayega.`
      },
      executiveEdge: {
        en: `Your professional edge lies in building scalable systems and managing complex, high-stakes responsibilities with quiet stamina. Where others panic under prolonged pressure, your natural endurance and structured thinking keep the ship steady.`,
        hi: `कार्यक्षेत्र में आपकी सबसे बड़ी ताकत व्यवस्थित ढांचा तैयार करना और जटिल जिम्मेदारियों को धैर्यपूर्वक संभालना है। जहां अन्य लोग दबाव में विचलित हो जाते हैं, वहीं आपका ठहराव और कार्यकुशलता स्थिति को संभाल लेती है।`,
        hinglish: `Workplace mein aapka sabse bada plus point hai aapka thehraav aur crisis handling. Jab baaki log panic karte hain, aap quietly excel spreadsheet aur step-by-step action plan ready kar dete ho. Long-term projects aur heavy responsibilities aapki natural strength hain.`
      },
      emotionalWiring: {
        en: `With Moon in ${moonSign} (${moonNak}), your emotional world is carefully protected. You take time to trust, but once committed, your loyalty is unbreakable. You express care through practical acts of service and financial security rather than empty verbal praise.`,
        hi: `चंद्रमा ${moonSign} (${moonNak}) में होने से आप अपनी भावनाओं को सहजता से उजागर नहीं करते। आप विश्वास करने में समय लेते हैं, किंतु एक बार संबंध बन जाने पर आपकी निष्ठा अडिग रहती है। आप शब्दों के स्थान पर व्यावहारिक सहायता और सुरक्षा देकर अपना अपनत्व प्रकट करते हैं।`,
        hinglish: `Moon ${moonSign} mein hone se aap apne emotions ko cover karke rakhte ho. Har kisi ko apne personal space mein entry nahi milti. Par jinke sath aapka vishwas jud gaya, unke liye aap pahaad ki tarah khade rehte ho—bina kisi dikhawe ke practical help karte ho.`
      },
      decisionStyleUnderPressure: {
        en: `In moments of acute stress, you retreat into analytical stillness. You strip away emotion and focus entirely on the operational reality. You prefer delayed perfection over rushed compromises.`,
        hi: `तनाव की स्थिति में आप शांत होकर वस्तुस्थिति का विश्लेषण करते हैं। आप भावुकता को परे रखकर केवल ठोस तथ्यों और समाधानों पर ध्यान केंद्रित करते हैं।`,
        hinglish: `Pressure aate hi aapka first instinct hota hai emotionally detach hoke facts dekhna. Jaldbazi mein faisla lena aapki dictionary mein nahi hai—aap thoda time lenge, par jo faisla karenge wo final aur pakka hoga.`
      },
      karmicBlindspotAndFix: {
        en: `Karmic Blindspot: Risk of becoming overly rigid, cynical, or micro-managing when systems fall short. Remedy: Intentionally delegate minor tasks, allow room for spontaneous play, and trust others to learn from their own operational mistakes.`,
        hi: `सावधानी एवं उपाय: आवश्यकता से अधिक कठोर होने या हर छोटी बात को नियंत्रित करने की प्रवृत्ति से बचें। कार्य दूसरों को सौंपना सीखें और जीवन में सहजता व विश्राम के लिए भी समय निकालें।`,
        hinglish: `Kahan sambhalna hai: Kabhi-kabhi perfectionism aur over-control aapko mentally exhaust kar deta hai. Sab kuch akele theek karne ki zidd chhodke team ko trust karo, aur thoda sa light-hearted time enjoy karna shuru karo.`
      },
      lifeAreaConversationalAdvice: {
        career: {
          en: `Focus on infrastructure, enterprise architecture, capital management, and institutional scaling.`,
          hi: `प्रबंधन, वित्तीय नियोजन, संरचनात्मक विकास एवं दीर्घकालिक संस्थागत कार्यों में विशेष सफलता।`,
          hinglish: `Operations, finance, real estate ya tech infrastructure jaise sectors jahan solid processes chahiye, wahan aap naturally lead karoge.`
        },
        wealth: {
          en: `Your wealth accumulates through compounding, hard assets, and steady investments rather than speculative gambles.`,
          hi: `धन संचय क्रमिक बचत, अचल संपत्ति और सुरक्षित निवेशों के माध्यम से सुदृढ़ होगा।`,
          hinglish: `Fatafat paisa banane ki scheme se bacho; compounding aur asset-building aapko generational wealth degi.`
        },
        relationships: {
          en: `Seek a partner who values mutual respect, shared financial vision, and emotional maturity over superficial drama.`,
          hi: `पारस्परिक सम्मान, साझा दृष्टिकोण और व्यावहारिक समझदारी वाले संबंधों को प्राथमिकता दें।`,
          hinglish: `Dono taraf se clear understanding aur practical support ho tabhi aap khush rahoge; drama aur unpredictable log aapki energy drain karte hain.`
        },
        growth: {
          en: `Practice letting go of outcome anxiety; structure is a tool to serve your peace, not a cage to trap you.`,
          hi: `परिणामों की अत्यधिक चिंता से मुक्त होकर वर्तमान कर्म की शुद्धि पर ध्यान दें।`,
          hinglish: `Roz ke routine mein thoda space rakho. Har cheez schedule ke mutabik na bhi chale toh chill karna seekho.`
        }
      }
    };
  }

  if (archetype.category === 'MARS_SUN_EXECUTIVE') {
    return {
      archetype,
      coreNature: {
        en: `Your chart runs on high agency and bold executive courage. With ${ascSign} ascendant fueled by fire and martial energy, you hate waiting for permission. You are a natural initiator who thrives when taking full ownership and pushing initiatives from the front line.`,
        hi: `आपकी कुंडली का स्वरूप प्रखर तेज, अदम्य साहस और नेतृत्व-क्षमता से परिपूर्ण है। ${ascSign} लग्न के प्रभाव से आप पहल करने वाले और त्वरित क्रियान्वयन में विश्वास रखने वाले व्यक्ति हैं। आप प्रतीक्षा करने के बजाय स्वयं आगे बढ़कर जिम्मेदारी संभालना पसंद करते हैं।`,
        hinglish: `Aapki kundali high-octane agency aur junoon se bhari hai. ${ascSign} lagna aur dynamic energy ke sath aap kisi ke order ka wait nahi karte. Aapka natural instinct 'front-foot' pe khelne ka hai. Problem aayi toh seedha face karna aur solution nikalna aapki pehchaan hai.`
      },
      executiveEdge: {
        en: `You cut through bureaucratic bottlenecks effortlessly. Your instinct for decisive timing and fearless confrontation gives you a massive advantage during turnaround situations, competitive markets, and zero-to-one startups.`,
        hi: `आप बाधाओं को तेजी से पार करते हुए स्पष्ट निर्णय लेते हैं। संकटकालीन परिस्थितियों और प्रतिस्पर्धी क्षेत्रों में आपका त्वरित निर्णय और निर्भीकता असाधारण लाभ देती है।`,
        hinglish: `Corporate ya business mein jahan log discussion mein fas jaate hain, aap wahan seedha decision leke execution start kar dete ho. Fast-moving environments aur turnaround projects aapke liye bane hain.`
      },
      emotionalWiring: {
        en: `With Moon in ${moonSign} (${moonNak}), your emotions are intense, transparent, and direct. You appreciate unfiltered honesty and cannot stand passive-aggressive games. When you care, you protect fiercely with all your might.`,
        hi: `चंद्रमा ${moonSign} (${moonNak}) में होने से आपकी भावनाएं स्पष्ट और पारदर्शी हैं। आप कपट और टालमटोल सहन नहीं करते। आप जिससे स्नेह करते हैं, उसकी ढाल बनकर हर परिस्थिति में रक्षा करते हैं।`,
        hinglish: `Moon ${moonSign} mein hone se aap dil ke ekdum saaf aur direct ho. Jo hai muh pe bolte ho, peeth peeche baat karna aapki fitrat nahi. Jinse pyar hai, unke liye jaan hazir, bas loyalty aur seedhi baat honi chahiye.`
      },
      decisionStyleUnderPressure: {
        en: `Under pressure, your adrenaline turns into sharp laser focus. You take decisive commands rather than getting paralyzed by excessive over-thinking.`,
        hi: `दबाव की स्थिति में आपका संकल्प और अधिक प्रखर हो जाता है। आप दुविधा में उलझने के बजाय त्वरित कदम उठाते हैं।`,
        hinglish: `Crisis aate hi aap panic nahi karte, balki attack mode mein aa jate ho. Jo cheez control mein hai usko capture karte ho aur aage badhte ho.`
      },
      karmicBlindspotAndFix: {
        en: `Karmic Blindspot: Impatience with slower teammates or acting on hot-headed impulse before all variables are clear. Remedy: Implement a mandatory 24-hour cooling window before firing off high-stakes responses or life-altering ultimatums.`,
        hi: `सावधानी एवं उपाय: जल्दबाजी में निर्णय लेने या दूसरों की धीमी गति पर अधीर होने से बचें। महत्वपूर्ण निर्णयों से पूर्व 24 घंटे का विश्राम लें ताकि विवेक पूर्णतः सक्रिय रहे।`,
        hinglish: `Kahan sambhalna hai: Gusse ya excitement mein aakar instant decision mat lo. 24 ghante ka pause button dabao, fir dekho ki jo step lene ja rahe ho wo long-term mein faydemand hai ya sirf temporary aag.`
      },
      lifeAreaConversationalAdvice: {
        career: {
          en: `Venture creation, executive leadership, defense, surgery, litigation, high-growth technology, and sports.`,
          hi: `नेतृत्व, प्रशासनिक अधिकार, उद्यमिता, तकनीकी नवोन्मेष और प्रतिस्पर्धात्मक क्षेत्रों में सर्वोच्च उन्नति।`,
          hinglish: `Startup founder, core management, field leadership ya challenging roles jahan daily wins chahiye, wahan aap chamkoge.`
        },
        wealth: {
          en: `Wealth comes through daring enterprise, bold calculated equity bets, and aggressive capital generation. Protect gains by locking portions into liquid reserves.`,
          hi: `साहसिक उद्यमों और सक्रिय निवेशों से धन लाभ होगा। लाभ के एक हिस्से को सुरक्षित परिसंपत्तियों में संचित रखें।`,
          hinglish: `Paisa kamane ki speed zabardast hai, bas savings ko lock karke rakho taaki jaldbazi mein reinvest na ho jaye.`
        },
        relationships: {
          en: `You need a self-assured partner who has their own fierce identity and won't feel overshadowed by your high drive.`,
          hi: `एक स्वाभिमानी, स्पष्टवादी और आत्मनिर्भर जीवनसाथी आपके व्यक्तित्व के साथ सर्वश्रेष्ठ संतुलन बनाएगा।`,
          hinglish: `Partner aisa hona chahiye jiska apna khud ka strong persona ho, jo aapko respect de par aapke gusse ko playfully handle karna jaanta ho.`
        },
        growth: {
          en: `Channel excess martial fire into disciplined physical workouts and breathwork to keep your mental clarity pristine.`,
          hi: `प्रतिदिन शारीरिक व्यायाम और प्राणायाम के माध्यम से अपनी आंतरिक ऊर्जा को संतुलित रखें।`,
          hinglish: `Daily gym, sports ya pranayama mandatory hai; physical energy burn hogi toh dimaag crystal clear rahega.`
        }
      }
    };
  }

  if (archetype.category === 'MERCURY_VENUS_AGILE') {
    return {
      archetype,
      coreNature: {
        en: `Your chart shines with agile intellect, social charm, and strategic versatility. With ${ascSign} ascendant and strong Mercury/Venus signatures, you are a master of smart articulation, commercial instinct, and cross-disciplinary problem-solving.`,
        hi: `आपकी कुंडली कुशाग्र मेधा, वाक-चातुर्य और कलात्मक दृष्टिकोण से सुशोभित है। ${ascSign} लग्न तथा बुध-शुक्र के प्रभाव से आप संवाद, बौद्धिक रणनीति, व्यावसायिक सूझबूझ और बहुआयामी समाधानों में निपुण हैं।`,
        hinglish: `Aapki kundali ka superpower hai aapka sharp dimaag aur zabardast communication finesse. ${ascSign} lagna ke sath aap kisi bhi situation ko smart negotiation aur articulate tareeqe se apne favour mein mould kar lete ho. Networking aur smart jugaad aapke baye haath ka khel hai.`
      },
      executiveEdge: {
        en: `Your biggest strength is seeing hidden patterns, connecting disparate ideas, and negotiating win-win outcomes. You excel at turning complex information into simple, captivating narratives.`,
        hi: `जटिल जानकारियों को सरल बनाना, नई संभावनाओं को जोड़ना और सौहार्दपूर्ण संवाद द्वारा परिणाम निकालना आपकी मुख्य विशेषता है।`,
        hinglish: `Aap multiple cheezein ek sath handle kar sakte ho. Complex problem ko decode karke simple banana aur right audience ko pitch karna aapka real edge hai.`
      },
      emotionalWiring: {
        en: `With Moon in ${moonSign} (${moonNak}), you process emotions intellectually. You crave engaging conversations, mental stimulation, and harmony. Crude environments and endless emotional drama exhaust you quickly.`,
        hi: `चंद्रमा ${moonSign} (${moonNak}) में होने से आप भावनाओं को बौद्धिक स्तर पर समझते हैं। आपको सार्थक संवाद, सुरुचिपूर्ण वातावरण और विचारों की समरसता प्रिय है।`,
        hinglish: `Moon ${moonSign} mein hone se aapko intellectual connect chahiye. Agar baat karne ke liye ache topics aur shared wavelength nahi hai, toh aap easily bore ho jaate ho. Arguments se zyada solution-oriented discussion pasand hai.`
      },
      decisionStyleUnderPressure: {
        en: `Under pressure, you rely on rapid mental pivots and tactical adjustments. You always keep a Plan B and Plan C ready in your pocket.`,
        hi: `कठिन परिस्थितियों में आप अपनी त्वरित सोच और लचीलेपन से नए विकल्प तैयार कर लेते हैं।`,
        hinglish: `Pressure aate hi aapka dimaag 5 alag options soch leta hai. Aap kisi ek raste pe faste nahi, adaptable banke alternative route nikal lete ho.`
      },
      karmicBlindspotAndFix: {
        en: `Karmic Blindspot: Tendency to over-analyze, scatter energy across too many parallel interests, or avoid necessary friction. Remedy: Choose one primary anchor project per quarter and commit to it until the final finish line.`,
        hi: `सावधानी एवं उपाय: एक समय में बहुत सारी योजनाओं में ऊर्जा बिखेरने से बचें। एक निश्चित लक्ष्य चुनकर उसे पूर्णता तक पहुंचाने का संकल्प लें।`,
        hinglish: `Kahan sambhalna hai: Overthinking aur ek sath 10 naav mein pair rakhne se bacho. Ek quarter mein ek main project ko 100% complete karne ka discipline banao.`
      },
      lifeAreaConversationalAdvice: {
        career: {
          en: `Fintech, media, high-level consulting, product design, diplomacy, trading, and communications.`,
          hi: `परामर्श, मीडिया, वाणिज्य, प्रौद्योगिकी, कूटनीति और संचार-प्रधान क्षेत्रों में उत्कृष्ट सफलता।`,
          hinglish: `Consulting, product strategy, marketing, trading ya digital media jahan dimaag aur presentation dono chahiye.`
        },
        wealth: {
          en: `Multiple income streams through intellectual property, brand equity, smart trading, and strategic partnerships.`,
          hi: `बौद्धिक कौशल, विविध आय स्रोतों और रणनीतिक साझेदारियों से प्रचुर धन लाभ।`,
          hinglish: `Multiple streams of income aapke liye best hain; knowledge aur network ko monetize karna aapka main wealth driver hai.`
        },
        relationships: {
          en: `You flourish with a partner who is witty, intellectually stimulating, and appreciates refined aesthetics.`,
          hi: `एक सुरुचिपूर्ण, बुद्धिमान और जीवंत साथी आपके जीवन को आनंद और प्रेरणा से भर देगा।`,
          hinglish: `Partner aisa ho jiske sath late night intellectual baatein ho sakein aur jo life ke aesthetic side ko appreciate kare.`
        },
        growth: {
          en: `Regular digital detoxes and silent walks in nature will prevent your hyper-active mental circuits from burning out.`,
          hi: `नियमित ध्यान और प्रकृति के सान्निध्य से अपने मानसिक तनाव को शांत और एकाग्र रखें।`,
          hinglish: `Screen time thoda cut karo aur haftay mein ek din silence practice karo, dimaag ki battery recharge ho jayegi.`
        }
      }
    };
  }

  // JUPITER_MOON_INTUITIVE
  return {
    archetype,
    coreNature: {
      en: `Your chart carries the sacred vibration of wisdom, mentorship, and principled depth. With ${ascSign} ascendant under Jupiter and Moon's benevolent stewardship, you are motivated by meaningful legacy, ethical integrity, and nurturing those around you.`,
      hi: `आपकी कुंडली उच्च संस्कारों, सहज ज्ञान और मार्गदर्शक व्यक्तित्व की द्योतक है। ${ascSign} लग्न और गुरु-चंद्र के शुभ प्रभाव से आप केवल तात्कालिक लाभ के लिए नहीं, बल्कि स्थायी प्रतिष्ठा, नैतिक मूल्यों और समाज कल्याण के लिए कार्य करते हैं।`,
      hinglish: `Aapki kundali ka core 'Apnapan aur Vishwas' pe tika hai. ${ascSign} lagna aur Jupiter-Moon guidance se aapka approach long-term legacy banane ka hai. Shortcuts aur unprincipled logon se aap naturally door rehte ho. Log aapke paas guidance aur bharose ke liye aate hain.`
    },
    executiveEdge: {
      en: `Your superpower is long-term vision and instinctive foresight. You can sense trends before they materialize and earn unconditional loyalty because people trust your moral compass.`,
      hi: `दीर्घकालिक दृष्टि और सटीक अंतःप्रेरणा आपकी सबसे बड़ी शक्ति है। लोग आपकी सत्यनिष्ठा और न्यायप्रियता पर अटूट विश्वास करते हैं।`,
      hinglish: `Aapka vision 5-10 saal aage ka dekh leta hai. Log aapki baat isliye maante hain kyunki aapki advice unbiased aur deeply rooted hoti hai. Advisory aur mentorship roles mein aap unbeatable ho.`
    },
    emotionalWiring: {
      en: `With Moon in ${moonSign} (${moonNak}), you feel deeply and possess an empathetic radar. You absorb the emotional climate around you and find deep fulfillment in uplifting others.`,
      hi: `चंद्रमा ${moonSign} (${moonNak}) में होने से आपका हृदय अत्यंत संवेदनशील और परोपकारी है। आप दूसरों की मनोदशा को सहज ही भांप लेते हैं।`,
      hinglish: `Moon ${moonSign} mein hone se aapka emotional radar bohot sharp hai. Kisi ke bina bole bhi aap samajh jaate ho ki saamne wale ke dil mein kya chal raha hai. Par doosron ki tension apne sar lena thoda kam karo.`
    },
    decisionStyleUnderPressure: {
      en: `When crises strike, you consult your inner ethical compass and spiritual center. You ask, 'What is the righteous long-term path?' rather than seeking convenient shortcuts.`,
      hi: `कठिन समय में आप अपने अंतर्मन और सिद्धांतों की सुनते हैं। आप तात्कालिक लाभ के स्थान पर मर्यादा और धर्म के मार्ग को चुनते हैं।`,
      hinglish: `Pressure mein aap shallow solutions nahi dhoondhte. Aapka sawal hota hai: 'Sahi rasta kya hai jisse sabka bhala ho?' Yahi dharmi approach aapko bacha ke rakhta hai.`
    },
    karmicBlindspotAndFix: {
      en: `Karmic Blindspot: Absorbing too much emotional baggage from others, or staying in unappreciative environments out of sheer moral obligation. Remedy: Establish firm personal boundaries and remember that saying 'no' to toxicity is a spiritual duty.`,
      hi: `सावधानी एवं उपाय: अत्यधिक परोपकार में स्वयं की उपेक्षा करने से बचें। अपने व्यक्तिगत समय और आत्म-सम्मान की रक्षा के लिए स्पष्ट सीमाएं तय करें।`,
      hinglish: `Kahan sambhalna hai: Har kisi ki problem solve karne mein apni energy drain mat karo. Clear boundaries banao aur 'na' bolna seekho jab koi aapke achhepan ka faayda uthaye.`
    },
    lifeAreaConversationalAdvice: {
      career: {
        en: `Higher education, institutional leadership, judiciary, healthcare, spiritual philosophy, wealth advisory, and transformative counseling.`,
        hi: `शिक्षा, न्याय, नीति-निर्माण, उच्च परामर्श, चिकित्सा और आध्यात्मिक मार्गदर्शन में सर्वोच्च सम्मान।`,
        hinglish: `Education, legal/ethical advisory, wealth management, teaching ya healthcare jahan aapka guidance logo ki life change kare.`
      },
      wealth: {
        en: `Wealth arrives steadily through dharmic pursuits, ethical enterprise, and blessed mentorship. Generosity multiplies your prosperity.`,
        hi: `सत्यनिष्ठ प्रयासों, ज्ञान के प्रसार और लोक-कल्याणकारी कार्यों से स्थायी समृद्धि प्राप्त होगी।`,
        hinglish: `Dharmic aur clean business se aane wala paisa hi aapke paas tikega aur multiply hoga. Daan-punya aapki wealth ko protect karta hai.`
      },
      relationships: {
        en: `You seek deep soulful harmony, spiritual companionship, and mutual reverence rather than superficial social contracts.`,
        hi: `एक संस्कारवान, निष्ठावान और आध्यात्मिक रूप से परिपक्व जीवनसाथी आपको आत्मिक शांति प्रदान करेगा।`,
        hinglish: `Partner aisa hona chahiye jiske sath aapka spiritual aur mental connect ho, jo aapke values ki respect kare.`
      },
      growth: {
        en: `Daily mantra japa, reading profound timeless texts, and regular pilgrimages will continually refresh your luminous aura.`,
        hi: `प्रतिदिन इष्ट स्मरण, स्वाध्याय और सात्विक दिनचर्या से आपका अंतर्ज्ञान सदैव जागृत रहेगा।`,
        hinglish: `Roz 10-15 minute mantra sadhana ya meditation zaroor karo; aapka aura hamesha pure aur magnetic rahega.`
      }
    }
  };
}

/**
 * Generate humanized Hinglish summary for any Bhava (1 to 12)
 */
export function generateHumanizedBhavaReading(
  assessment: CompleteBhavaConditionAssessment,
  archetype: ChartArchetypeProfile
): {
  executiveHinglish: string;
  actionAdviceHinglish: string;
  tonePill: string;
} {
  const { meta, headerSummary, triPillar, advancedVedicMetrics } = assessment;
  const hNum = meta.houseNumber;
  const lordName = triPillar.manager.lordPlanet;
  const lordDignity = triPillar.manager.saptaVargaDignity;
  const savPoints = headerSummary.bavBinduCount;

  let tonePill = archetype.signatureKeywords[0] || 'Pragmatic';
  let execHinglish = '';
  let adviceHinglish = '';

  if (archetype.category === 'SATURN_EARTH_PRAGMATIC') {
    execHinglish = `House ${hNum} (${meta.sanskritName}) ka evaluation: Lord ${lordName} (${lordDignity}) aur ${savPoints} SAV bindus ke sath, yeh bhava solid thehraav aur grounded mehnat se manifest hoga. Zero drama, pura focus consistent systems banane par rakhein.`;
    adviceHinglish = `Action Step: Is house se jude mamlon mein short-cuts mat lijiye. Hisaab-kitaab clear rakhein aur systematic routine follow karein.`;
  } else if (archetype.category === 'MARS_SUN_EXECUTIVE') {
    execHinglish = `House ${hNum} (${meta.sanskritName}) ka execution engine: Lord ${lordName} (${lordDignity}) aur ${savPoints} bindus ke sath, yahan front-foot ownership aur clear boundaries chahiye. Junoon aur direct action se hi results deliver honge.`;
    adviceHinglish = `Action Step: Overthinking chhodke lead lijiye. Clear goals set karke aggressively aage badhein.`;
  } else if (archetype.category === 'MERCURY_VENUS_AGILE') {
    execHinglish = `House ${hNum} (${meta.sanskritName}) ka strategic potential: Lord ${lordName} (${lordDignity}) aur ${savPoints} bindus ke sath, yahan smart jugaad, articulation aur networking se maximum growth milegi.`;
    adviceHinglish = `Action Step: Communication aur right alliances par dhyan dein. Situation ko adapt karke multiple avenues explore karein.`;
  } else {
    execHinglish = `House ${hNum} (${meta.sanskritName}) ka dharmic blueprint: Lord ${lordName} (${lordDignity}) aur ${savPoints} bindus ke sath, yahan apnapan, vishwas aur long-term legacy build karne ka yoga hai.`;
    adviceHinglish = `Action Step: Apne inner ethical compass ko follow karein aur patient consistency ke sath right values uphold karein.`;
  }

  return {
    executiveHinglish: execHinglish,
    actionAdviceHinglish: adviceHinglish,
    tonePill
  };
}
